<script>
  import { onMount } from 'svelte';

  import { Button, DataTable, InlineNotification, NotificationActionButton, Toolbar, ToolbarContent } from 'carbon-components-svelte';

  import EditIcon from 'carbon-icons-svelte/lib/Edit16';
  import DeleteIcon from 'carbon-icons-svelte/lib/Delete16';
  import ViewIcon from 'carbon-icons-svelte/lib/View16';

  import AddOrEditVenueModal from './market-modal/AddOrEditVenueModal.svelte';
  import DeleteEntityModal from '../../components/DeleteEntityModal.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PassiveEntityTableModal from '../../components/PassiveEntityTableModal.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import ToolbarSearchWithRouting from '../../components/ToolbarSearchWithRouting.svelte';

  import { getVenues, deleteVenue } from '../../services';
  import { rowContainsText } from '../../utils';

  const tableHeaders = [
    { key: 'exchangeName', value: 'Exchange' },
    { key: 'countryCode', value: 'Country' },
    { key: 'mic', value: 'MIC' },
    { key: 'timezone', value: 'Timezone' },
    { key: 'actions', value: '', sort: false },
  ];

  let pageData = { loading: true, venues: [], error: null };
  let query = { currentPage: 1, pageSize: 25, searchText: '' };

  let modals = {
    addOrEditVenue: { open: false },
    viewAssociations: { open: false },
    deleteVenue: { open: false },
  };

  async function fetchVenues() {
    pageData = { ...pageData, loading: true, error: null };

    try {
      const venues = await getVenues();
      pageData = { ...pageData, venues, loading: false };
    } catch (error) {
      console.error('[Venues] Failed to load venues!', error);
      pageData = { ...pageData, loading: false, error };
    }
  }

  function updatePage(event) {
    const { action } = event;

    switch (action) {
      case 'add-venue':
      case 'edit-venue':
      case 'delete-venue':
        fetchVenues();
        break;
      default:
        console.warn(`Unknown action "${action}"! No handler implemented.`, { event });
    }
  }

  function onSearchTextChange(event) {
    query = { ...query, currentPage: 1, searchText: event?.detail ?? '' };
  }

  function openModal(name, forwardData = {}) {
    return function handleOpen() {
      modals = {
        ...modals,
        [name]: { open: true, ...forwardData },
      };
    };
  }

  function closeModal(name) {
    return function handleClose({ detail }) {
      if (detail) {
        updatePage(detail);
      }

      modals = {
        ...modals,
        [name]: { open: false },
      };
    };
  }

  onMount(fetchVenues);

  $: filteredVenues = pageData.venues.filter((venueRow) => {
    if (!query.searchText) {
      return true;
    }

    return rowContainsText(venueRow, query.searchText);
  });
</script>

{#if pageData.error}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle="Failed to load venues! Please try again later." />
{/if}

{#if !pageData.loading && !pageData.error && pageData.venues.length === 0}
  <InlineNotification hideCloseButton kind="info" lowContrast title="Info:" subtitle="No venues available yet.">
    <svelte:fragment slot="actions">
      <NotificationActionButton on:click={openModal('addOrEditVenue')}>Add first</NotificationActionButton>
    </svelte:fragment>
  </InlineNotification>
{:else}
  <LoadingSpinner loading={pageData.loading}>
    <DataTable class="mst-table--lock-end" headers={tableHeaders} page={query.currentPage} pageSize={query.pageSize} rows={filteredVenues ?? []} sortable>
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'actions'}
          <div class="actions">
            <Button
              portalTooltip
              iconDescription="Edit Venue"
              icon={EditIcon}
              size="small"
              kind="ghost"
              tooltipPosition="left"
              on:click={openModal('addOrEditVenue', { data: row })}
            />
            <Button
              portalTooltip
              iconDescription={`Associations (${row.feeds?.length ?? 0})`}
              icon={ViewIcon}
              size="small"
              kind="ghost"
              tooltipPosition="top"
              disabled={!(row.feeds?.length > 0)}
              on:click={openModal('viewAssociations', { data: row })}
            />
            <Button
              hideTooltip
              iconDescription="Delete Venue"
              icon={DeleteIcon}
              size="small"
              kind="danger-ghost"
              tooltipPosition="top"
              disabled={row.feeds?.length > 0}
              on:click={openModal('deleteVenue', { data: row })}
            />
          </div>
        {:else if cell.key === 'mic'}
          <strong>{cell.value}</strong>
        {:else}
          {cell.value || '—'}
        {/if}
      </span>
      <Toolbar>
        <ToolbarContent>
          <ToolbarSearchWithRouting
            placeholder="Search venues"
            persistent
            bind:searchText={query.searchText}
            on:toolbarSearchValueChange={onSearchTextChange}
          />
          <Button on:click={openModal('addOrEditVenue')}>Add venue</Button>
        </ToolbarContent>
      </Toolbar>
    </DataTable>
    <PaginationWithRouting
      pageSizes={[10, 25, 50]}
      totalItems={filteredVenues?.length ?? 0}
      bind:page={query.currentPage}
      bind:pageSize={query.pageSize}
    />
  </LoadingSpinner>
{/if}

{#if modals.addOrEditVenue.open}
  <AddOrEditVenueModal open {...modals.addOrEditVenue} on:close={closeModal('addOrEditVenue')} />
{/if}

{#if modals.viewAssociations.open}
  <PassiveEntityTableModal
    open
    modalHeading="Feed Associations"
    modalLabel={modals.viewAssociations.data.exchangeName}
    rows={modals.viewAssociations.data.feeds ?? []}
    createActionLink={(entity) => `/data-feed/${entity.id}`}
    on:close={closeModal('viewAssociations')}
  />
{/if}

{#if modals.deleteVenue.open}
  <DeleteEntityModal
    open
    entityType="Venue"
    entityData={modals.deleteVenue.data}
    entityDisplayName={modals.deleteVenue.data?.exchangeName}
    handler={deleteVenue}
    on:close={closeModal('deleteVenue')}
    on:delete-venue={closeModal('deleteVenue')}
  />
{/if}

<style>
  .actions {
    display: flex;
    justify-content: center;
  }

  .actions :global(.bx--btn--icon-only) {
    padding: 0 0.5rem !important;
  }

  .actions :global(.bx--btn--icon-only svg) {
    margin-left: 0;
  }

  .actions :global(.bx--btn--ghost:hover) {
    background-color: var(--cds-active-01);
  }
</style>
